/**
 * MarketInsightDetailContent — 纯渲染层
 *
 * 不依赖任何 store，所有数据均由 props 传入。
 * 供 MarketInsightDetail（store 驱动）和
 * MarketInsightDetailPanelForReportPage（半受控）共享。
 */

import { useCallback } from 'react';
import cn from 'classnames';
import { Skeleton } from '@leadsnavi/ui-kit';
import { X, RefreshCw } from 'lucide-react';
import type { ConfigType } from 'dayjs';
import mixpanel from 'mixpanel-browser';
import { formatEmailCardTime } from '@/utils/dayjs';
import { ReportActionButtons } from '../report-action-buttons';
import type { ReportContentBlock, ReportType, FollowStatus, ReportDetailData } from './types';
import {
  SubheadingBlock,
  SubtitleBlock,
  ParagraphBlock,
  InsightCalloutBlock,
  MetricCardsBlock,
  TableBlock,
  ChartBlock,
  ActionSuggestionBlock,
  TimelineBlock,
  WhatChangedBlock,
  BlockEmptyState,
} from './blocks';
import { VerdictCalloutBlock, RedFlagsBlock, EntityOverviewBlock, EvidenceListBlock } from './blocks/specialty-blocks';
import { BlockErrorBoundary } from '@/components/block-error-boundary';

// ─────────────────────────────────────────────
// Block 间距配置
// ─────────────────────────────────────────────

/** 单个 block 的上下间距（px） */
type BlockSpacing = { marginTop: number; marginBottom: number };

/** 未配置类型的 fallback 间距 */
const DEFAULT_BLOCK_SPACING: BlockSpacing = { marginTop: 0, marginBottom: 0 };

/**
 * 每种 block 类型的上下间距配置。
 * 键类型与 ReportContentBlock['type'] 完全对齐，新增 block 类型时 TypeScript 会报错提示补充。
 */
const BLOCK_SPACING_MAP: Record<ReportContentBlock['type'], BlockSpacing> = {
  // ── Market Trend & Inspector 通用 ──
  headline: { marginTop: 24, marginBottom: 8 },
  subheading: { marginTop: 20, marginBottom: 6 },
  subtitle: { marginTop: 32, marginBottom: 4 },
  paragraph: { marginTop: 12, marginBottom: 0 },
  insight_callout: { marginTop: 16, marginBottom: 0 },
  metric_cards: { marginTop: 16, marginBottom: 0 },
  table: { marginTop: 16, marginBottom: 0 },
  chart: { marginTop: 16, marginBottom: 0 },
  action_suggestion: { marginTop: 16, marginBottom: 0 },
  timeline: { marginTop: 16, marginBottom: 0 },
  what_changed: { marginTop: 16, marginBottom: 0 },
  // ── Inspector 专有 ──
  verdict_callout: { marginTop: 20, marginBottom: 0 },
  red_flags: { marginTop: 16, marginBottom: 0 },
  entity_overview: { marginTop: 16, marginBottom: 0 },
  evidence_list: { marginTop: 16, marginBottom: 0 },
};

// ─────────────────────────────────────────────
// Block 渲染分发器
// ─────────────────────────────────────────────

/** 根据 block type 渲染对应组件 */
function renderBlock(block: ReportContentBlock): React.ReactElement | null {
  if (!block?.type) return null;

  switch (block.type) {
    // ── Market Trend & Inspector 通用 ──
    case 'headline':
      // headline 为标题，已经通过 title 渲染，无需重复渲染
      return null;
    case 'subheading':
      return <SubheadingBlock block={block} />;
    case 'subtitle':
      return <SubtitleBlock block={block} />;
    case 'paragraph':
      return <ParagraphBlock block={block} />;
    case 'insight_callout':
      return <InsightCalloutBlock block={block} />;
    case 'metric_cards':
      return <MetricCardsBlock block={block} />;
    case 'table':
      return <TableBlock block={block} />;
    case 'chart':
      return <ChartBlock block={block} />;
    case 'action_suggestion':
      return <ActionSuggestionBlock block={block} />;
    case 'timeline':
      return <TimelineBlock block={block} />;
    case 'what_changed':
      return <WhatChangedBlock block={block} />;
    // ── Inspector 专有 ──
    case 'verdict_callout':
      return <VerdictCalloutBlock block={block} />;
    case 'red_flags':
      return <RedFlagsBlock block={block} />;
    case 'entity_overview':
      return <EntityOverviewBlock block={block} />;
    case 'evidence_list':
      return <EvidenceListBlock block={block} />;
    default:
      console.error(`Unknown block type: ${block}`);
      return null;
  }
}

// ─────────────────────────────────────────────
// 报告类型标签
// ─────────────────────────────────────────────
const REPORT_TYPE_LABEL: Record<1 | 2, string> = {
  1: 'Market Trend',
  2: 'Company Audit',
};

// ─────────────────────────────────────────────
// Props 类型
// ─────────────────────────────────────────────

export type MarketInsightDetailContentProps = {
  showHeader?: boolean;
  // ── 展示数据 ──
  title: string;
  summary: string;
  reportType: ReportType;
  createdAt: ConfigType;
  detail: ReportDetailData | null;
  detailLoading: boolean;
  detailError: string | null;
  // ── 受控：收藏 ──
  isSaved: boolean;
  saveLoading: boolean;
  onToggleSave: () => void;
  // ── 受控：关注 ──
  followStatus: FollowStatus;
  followLoading: boolean;
  onToggleFollow: () => void;
  // ── 面板控制 ──
  onClose: () => void;
  onRetry: () => void;
  className?: string;
};

// ─────────────────────────────────────────────
// 主组件
// ─────────────────────────────────────────────

export function MarketInsightDetailContent({
  title,
  summary,
  reportType,
  createdAt,
  detail,
  detailLoading,
  detailError,
  isSaved,
  saveLoading,
  onToggleSave,
  followStatus,
  followLoading,
  onToggleFollow,
  onClose,
  onRetry,
  className,
  showHeader = true,
}: MarketInsightDetailContentProps) {
  const typeLabel = REPORT_TYPE_LABEL[reportType];

  const handleRetry = useCallback(() => {
    onRetry();
  }, [onRetry]);

  const handleToggleSave = useCallback(() => {
    mixpanel.track('report_content_collect');
    onToggleSave();
  }, [onToggleSave]);

  const handleToggleFollow = useCallback(() => {
    mixpanel.track('report_content_subscribe');
    onToggleFollow();
  }, [onToggleFollow]);

  // ── 渲染加载态 ──
  if (detailLoading) {
    return (
      <div className={cn('flex flex-col h-full bg-white', className)}>
        <div className="flex items-center justify-between p-4 border-b border-[#F3F4F6]">
          <Skeleton className="h-5 w-1/2 rounded" />
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-100 transition-colors">
            <X size={18} className="text-[#737373]" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex flex-col gap-2">
              <Skeleton className="h-4 w-1/3 rounded" />
              <Skeleton className="h-16 w-full rounded" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── 渲染错误态 ──
  if (detailError) {
    return (
      <div className={cn('flex flex-col h-full bg-white', className)}>
        <div className="flex items-center justify-between p-4 border-b border-[#F3F4F6]">
          <span className="text-[14px] font-medium text-[#09090B]">{title}</span>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-gray-100 transition-colors">
            <X size={18} className="text-[#737373]" />
          </button>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center gap-3 p-6">
          <p className="text-[14px] text-[#737373] text-center">{detailError}</p>
          <button
            type="button"
            onClick={handleRetry}
            className="flex items-center gap-1.5 px-4 py-2 text-[13px] text-[#262626] border border-[#E5E5E5] rounded-[8px] hover:bg-gray-50 transition-colors"
          >
            <RefreshCw size={14} />
            重试
          </button>
        </div>
      </div>
    );
  }

  // ── 渲染正常态 ──
  const blocks = detail?.contentBlocks ?? [];

  return (
    <div className={cn('flex flex-col h-full bg-white', className)}>
      {/* ── header1：操作按钮区 + 关闭（Figma: node 10449-26252） ── */}
      {showHeader ? (
        <div className="flex items-center justify-between px-4 py-4 border-b border-[#E5E5E5]">
          {/* 左侧操作按钮组（收藏 + 关注，复用公共组件） */}
          <ReportActionButtons
            isSaved={isSaved}
            saveLoading={saveLoading}
            followStatus={followStatus}
            followLoading={followLoading}
            onToggleSave={handleToggleSave}
            onToggleFollow={handleToggleFollow}
            reverseOrder={true}
          />
          {/* 关闭按钮 */}
          <button
            type="button"
            onClick={onClose}
            className="flex items-center justify-center w-7 h-7 rounded hover:bg-gray-100 transition-colors cursor-pointer"
            aria-label="关闭详情"
          >
            <X size={24} className="text-[#000000]" />
          </button>
        </div>
      ) : null}

      {/* ── 可滚动内容区：线性顺序渲染所有 blocks ── */}
      <div className="flex-1 overflow-y-auto pb-5 p-6">
        {/* ── header2：Badge 类型 + 日期（Figma: node 10449-26275） ── */}
        <div className="flex items-center justify-between">
          {typeLabel && (
            <span className="inline-flex items-center gap-1 px-2 h-6 text-[12px] font-medium leading-[16px] bg-[#D1FAE5] text-[#065F46] rounded-[8px]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#065F46] flex-shrink-0" />
              {typeLabel}
            </span>
          )}
          <span className="text-[12px] leading-[150%] tracking-[0.015em] text-[#737373]">
            {formatEmailCardTime(createdAt)}
          </span>
        </div>
        {/* ── header3：大标题区（Figma: node 10449-26273） ── */}
        <h1 className="text-[24px] mt-6 mb-5 font-semibold leading-[120%] tracking-[-0.02em] text-[#262626]">
          {title}
        </h1>

        {/* ── 摘要（可选）── */}
        {summary && <p className="text-[14px] leading-[150%] tracking-[0.005em] text-[#737373]">{summary}</p>}
        {blocks.length === 0 ? (
          <BlockEmptyState label="报告内容" />
        ) : (
          blocks.map((block, idx) => {
            const rendered = renderBlock(block);
            if (!rendered) return null;
            const spacing = BLOCK_SPACING_MAP[block.type] ?? DEFAULT_BLOCK_SPACING;
            return (
              <div
                key={idx}
                className="w-full"
                data-block-type={block.type}
                style={{ marginTop: spacing.marginTop, marginBottom: spacing.marginBottom }}
              >
                <BlockErrorBoundary>{rendered}</BlockErrorBoundary>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
